import React, { KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { Button, Heading } from '@placer-ui/components';
import { useForm } from 'react-hook-form';
import { ControlledInputField } from 'components/controlled-input-field';
import {
    companyRules,
    firstNameRules,
    jobTitleRules,
    lastNameRules,
    passwordRules,
    phoneRules,
} from 'features/authentication/signup/validations/validations';
import { PasswordFieldController } from 'features/authentication/signup/components/password-field-controller/password-field-controller';
import { useSplunkCallback } from 'hooks/use-splunk-callback/use-splunk-callback';
import {
    BACKEND_EMAIL_ALREADY_REGISTERED_ERROR_CODE,
    BACKEND_INVALID_CREDENTIALS_ERROR_CODE,
    signupBaseSplunkData,
} from 'features/authentication/utils/constants';
import { useQuery, useRouteParam } from 'hooks/use-query/use-query';
import {
    sendHubspotGoogleSignupForm,
    sendHubspotSignupForm,
} from 'features/authentication/signup/utils/signup-hubspot';
import { useAuthActions } from 'store/auth/use-auth-actions';
import { heapIdentify, heapTrack } from 'utils/heap/heap';
import { AuthError } from 'features/authentication/utils/auth-error';
import { getUrlToForceSso } from 'features/authentication/utils/sso/sso';
import { useHistory, useLocation } from 'react-router-dom';
import { setDataOriginCookie, setDocumentTitle } from 'features/authentication/utils/utils';
import { SIGNUP_CONFIRM_EMAIL_STEP_ROUTE } from 'features/authentication/signup/constants/signup-links';
import { useGoBackToEmailStep } from 'features/authentication/signup/hooks/navigation';
import { shouldRenderMobileApp } from 'utils/detect-environment';
import { userAPI } from 'API/user-api';
import styles from './signup-full-form-step.module.scss';

export type SignupFullFormStepProps = {
    serverError: AuthError | undefined;
    setServerError: (error: AuthError | undefined) => void;
    userEmail: string;
    userCredentials: Omit<SignupFullStepForm, 'password'>;
    setUserCredentials: (values: Omit<SignupFullStepForm, 'password'>) => void;
};

export type SignupFullStepForm = {
    firstName: string;
    lastName: string;
    company: string;
    jobTitle: string;
    phone: string;
    password: string;
};

export type GoogleSignupFullStepForm = Pick<SignupFullStepForm, 'company' | 'phone' | 'jobTitle'>;

const PAGE_TITLE = 'Set up your account';

export const SignupFullFormStep = ({
    userEmail,
    setServerError,
    userCredentials,
    setUserCredentials,
}: SignupFullFormStepProps) => {
    const [loading, setLoading] = useState(false);
    const sendEvent = useSplunkCallback(signupBaseSplunkData);
    const { signup } = useAuthActions();
    const nextUrl = useQuery('next_url') || '';
    const history = useHistory();
    const location = useLocation();
    const goBackToEmailStep = useGoBackToEmailStep();
    const isMobile = shouldRenderMobileApp();
    const isGoogleAuthenticated = useRouteParam('google_auth');
    const { loginAuthenticatedUser } = useAuthActions();

    useEffect(() => {
        setDocumentTitle(PAGE_TITLE);
        window.scrollTo(0, 0);
    }, []);

    const goBack = useCallback(() => {
        setServerError(undefined);
        goBackToEmailStep();
    }, [setServerError, goBackToEmailStep]);

    useEffect(() => {
        if (!userEmail) goBack();
    }, [goBack, userEmail]);

    const urlUtmParams = {
        utm_source: useQuery('utm_source'),
        utm_medium: useQuery('utm_medium'),
        utm_campaign: useQuery('utm_campaign'),
    };

    const { control, handleSubmit } = useForm<SignupFullStepForm>({
        defaultValues: {
            company: userCredentials.company,
            jobTitle: userCredentials.jobTitle,
            firstName: userCredentials.firstName,
            lastName: userCredentials.lastName,
            password: '',
            phone: userCredentials.phone,
        },
        mode: 'onChange',
    });

    const onSubmit = async (values: SignupFullStepForm) => {
        setServerError(undefined);
        setLoading(true);
        sendEvent({ action: 'signup-page-signup-click' });
        sendHubspotSignupForm(userEmail, 'regular', values, urlUtmParams);
        setUserCredentials({
            phone: values.phone,
            company: values.company,
            jobTitle: values.jobTitle,
            firstName: values.firstName,
            lastName: values.lastName,
        });

        try {
            /* call signup API only if not authenticated yet */
            if (!isGoogleAuthenticated) {
                await signup({
                    name: values.firstName + ' ' + values.lastName,
                    company: values.company,
                    password: values.password,
                    username: userEmail,
                    next_url: decodeURIComponent(nextUrl) ?? undefined,
                });

                heapIdentify(userEmail);
                sendEvent({
                    action: 'signup-success',
                    method: 'email',
                    email: userEmail,
                });
                heapTrack('Signup Success', {
                    method: 'email',
                });
                history.replace({
                    pathname: SIGNUP_CONFIRM_EMAIL_STEP_ROUTE,
                    search: location.search,
                });
            } else {
                if (!nextUrl) setDataOriginCookie();
                const formInputValues = {
                    company: values.company,
                    jobTitle: values.jobTitle,
                    phone: values.phone,
                };
                const userCookie = JSON.parse(localStorage.getItem('user') || '');
                await loginAuthenticatedUser(userCookie, nextUrl);
                sendHubspotGoogleSignupForm(userEmail, 'google', formInputValues);
                await userAPI.updateCompanyName(values.company);
            }
        } catch (e) {
            const authError = AuthError.fromUnknownError(e);
            if (authError.shouldForceSso) {
                const ssoPath = getUrlToForceSso(userEmail, authError.details?.account, nextUrl);
                history.push(ssoPath);
            } else {
                if (
                    [
                        BACKEND_INVALID_CREDENTIALS_ERROR_CODE,
                        BACKEND_EMAIL_ALREADY_REGISTERED_ERROR_CODE,
                    ].includes(authError.status)
                ) {
                    goBackToEmailStep();
                }
                setServerError(authError);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e: KeyboardEvent) => {
        if (e.charCode === 13) {
            e.preventDefault();
            handleSubmit(onSubmit)();
        }
    };

    const showAllFields = !isGoogleAuthenticated;

    return (
        <div className={styles.root}>
            <div className={styles.title}>
                <Heading variant="h2" testId="set-up-your-account-page-title">
                    {PAGE_TITLE}
                </Heading>
            </div>
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
                {showAllFields && (
                    <>
                        <ControlledInputField<SignupFullStepForm>
                            name="firstName"
                            control={control}
                            label={'First Name'}
                            placeholder="Enter your first name"
                            autoComplete="given-name"
                            onKeyPress={handleKeyPress}
                            rules={firstNameRules}
                            disabled={loading}
                            testId="signup-first-name-input"
                            asterix
                            required
                        />
                        <ControlledInputField<SignupFullStepForm>
                            name="lastName"
                            autoComplete="family-name"
                            label={'Last Name'}
                            control={control}
                            placeholder="Enter your last name"
                            onKeyPress={handleKeyPress}
                            rules={lastNameRules}
                            disabled={loading}
                            testId="signup-last-name-input"
                            asterix
                            required
                        />
                    </>
                )}
                <ControlledInputField<SignupFullStepForm>
                    name="company"
                    control={control}
                    label={'Company'}
                    autoComplete="organization"
                    placeholder="Enter company name"
                    onKeyPress={handleKeyPress}
                    rules={companyRules}
                    disabled={loading}
                    testId="signup-company-input"
                    asterix
                    required
                />
                <ControlledInputField<SignupFullStepForm>
                    name="jobTitle"
                    control={control}
                    label={'Job Title'}
                    autoComplete="Job Title"
                    placeholder="Enter your Job Title"
                    onKeyDown={handleKeyPress}
                    rules={jobTitleRules}
                    disabled={loading}
                    testId="signup-job-title-input"
                    asterix
                    required
                />
                <ControlledInputField<SignupFullStepForm>
                    name="phone"
                    type="tel"
                    control={control}
                    label={'Phone Number'}
                    placeholder="Enter your Phone Number"
                    onKeyPress={handleKeyPress}
                    rules={phoneRules}
                    disabled={loading}
                    autoComplete="tel"
                    testId="signup-phone-input"
                />
                {showAllFields && (
                    <PasswordFieldController<SignupFullStepForm>
                        name="password"
                        control={control}
                        label={'Password'}
                        placeholder="Enter a password"
                        onKeyPress={handleKeyPress}
                        rules={passwordRules}
                        disabled={loading}
                        autoComplete="new-password"
                        testId="signup-password-input"
                        required
                        asterix
                    />
                )}
                <div className={styles.buttonsContainer}>
                    <Button
                        testId="signup-full-form-step-submit-button"
                        variant={'primary-brand'}
                        fullWidth
                        isLoading={loading}
                        type="submit"
                    >
                        Get Access
                    </Button>
                    <div className={styles.backContainer}>
                        {!isMobile && (
                            <Button
                                testId="signup-full-form-step-back-button"
                                fullWidth
                                variant="text"
                                onClick={goBack}
                            >
                                Back
                            </Button>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
};
